import React, { useCallback, useEffect, useState } from "react";
import {
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Text,
} from "@chakra-ui/react";
import AppSelect from "@/features/line-sheet-sets/app-select";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppDatePicker from "@/features/line-sheet-sets/app-date-picker";
import AppInput from "@/features/ui/app-input";
import InvoiceFileInput from "@/features/invoices/invoice-file-input";
import AppFileDragAndDropZone from "@/features/invoices/app-file-drag-and-drop-zone";
import { isFile, isStoredObject, Price, StoredObject } from "@/features/types";
import useCreateTransportationPayment from "@/features/transportations/hooks/use-create-transportation-payment";
import useUpdateTransportationPayment from "@/features/transportations/hooks/use-update-transportation-payment";
import useStoreObject from "@/features/ui/hooks/use-store-object";
import { mutateIfMatched } from "@/features/ui/utils/swr-utils";
import { TransportationPayment } from "@/features/transportations/transportation-payment.type";
import AppRemittanceStatusLabel from "@/features/ui/app-remittance-status-label";
import useIdentity from "@/features/ui/hooks/use-identity";
import useParseTransportationPayment from "@/features/transportations/hooks/use-parse-transportation-payment";

interface Props {
  agencyId: number;
  transportationId: number;
  existing?: TransportationPayment | null;
  alwaysEdit?: boolean;
}

interface FormState {
  issuedOn: Date | null;
  file: File | Pick<StoredObject, "id" | "name" | "createdAt"> | null;
  amount: string;
  isRemitted: boolean;
  remittedOn: Date | null;
}

export interface PaymentFormState {
  issuedOn: Date;
  amount: string;
  isRemitted: boolean;
  remittedOn: Date | null;
  file: File | Pick<StoredObject, "id" | "name" | "createdAt"> | null;
}

export function TransportationPaymentInlineForm({
  value,
  onChange,
  isDisabled,
  onBlNumberParsed,
}: {
  value: PaymentFormState;
  onChange: (value: PaymentFormState) => void;
  isDisabled?: boolean;
  // 자금청구서에서 추출한 B/L 번호를 상위(운송 폼)로 전달
  onBlNumberParsed?: (blNumber: string) => void;
}) {
  const { tTitle } = useI18nHelper();
  const identity = useIdentity();
  const { info: showInfo, error: showError } = useAppToasts();
  const { fire: fireParsePayment } = useParseTransportationPayment();
  const [isParsing, setIsParsing] = useState(false);

  // 자금청구서(PDF) 첨부 시 백엔드로 보내 발행일자/금액을 추출해 자동 입력한다.
  const handleFileAdd = useCallback(
    async (file: File) => {
      // 우선 파일을 즉시 반영
      onChange({ ...value, file });

      const agencyId = identity?.company?.id;
      const isPdf =
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf");
      if (!agencyId || !isPdf) {
        return;
      }

      setIsParsing(true);
      try {
        const res = await fireParsePayment({ agencyId }, { file });
        if (res && (res.issuedOn || res.amount != null)) {
          onChange({
            ...value,
            file,
            issuedOn: res.issuedOn ? new Date(res.issuedOn) : value.issuedOn,
            amount: res.amount != null ? String(res.amount) : value.amount,
          });
          showInfo(tTitle("transportation_payment") + " 발행일자/금액을 자동 입력했습니다.");
        }
        if (res?.blNumber) {
          onBlNumberParsed?.(res.blNumber);
        }
      } catch (e) {
        // 추출 실패해도 파일 첨부는 유지하고 수동 입력 가능
        showError(e);
      } finally {
        setIsParsing(false);
      }
    },
    [identity, value, onChange, fireParsePayment, showInfo, showError, tTitle, onBlNumberParsed]
  );

  return (
    <Flex flexDirection={"column"} gap={"12px"}>
      <Grid templateColumns={"repeat(4, 1fr)"} gap={"12px"}>
        <FormControl>
          <FormLabel fontSize={"12px"} marginBottom={"4px"}>
            {tTitle("issued_on")}
          </FormLabel>
          <AppDatePicker
            value={value.issuedOn}
            showTimeInput={false}
            onChange={(v) => onChange({ ...value, issuedOn: v ?? new Date() })}
            isDisabled={isDisabled}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize={"12px"} marginBottom={"4px"}>
            {tTitle("amount")} (KRW)
          </FormLabel>
          <AppInput
            value={value.amount}
            type={"number"}
            onChange={(v) => onChange({ ...value, amount: v ?? "" })}
            isDisabled={isDisabled}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize={"12px"} marginBottom={"4px"}>
            {tTitle("remittance_status")}
          </FormLabel>
          <AppSelect<string>
            name={tTitle("remittance_status")}
            width={"100%"}
            options={[
              { value: "false", name: tTitle("remittance_status.remittance_required") },
              { value: "true", name: tTitle("remittance_status.remitted") },
            ]}
            value={value.isRemitted ? "true" : "false"}
            onSelect={(v) => onChange({
              ...value,
              isRemitted: v === "true",
              remittedOn: v === "true" ? value.remittedOn ?? new Date() : null,
            })}
            isDisabled={isDisabled}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize={"12px"} marginBottom={"4px"}>
            {tTitle("remitted_on")}
          </FormLabel>
          <AppDatePicker
            name={""}
            value={value.remittedOn ?? undefined}
            showTimeInput={false}
            onChange={(v) => onChange({ ...value, remittedOn: v ?? null })}
            isDisabled={isDisabled || !value.isRemitted}
          />
        </FormControl>
      </Grid>
      <FormControl>
        <FormLabel fontSize={"12px"} marginBottom={"4px"}>
          {tTitle("file")}
          {isParsing && (
            <Text as={"span"} fontSize={"11px"} color={"#6F6F6C"} marginLeft={"6px"}>
              발행일자/금액 추출 중…
            </Text>
          )}
        </FormLabel>
        {!value.file && (
          <AppFileDragAndDropZone
            isMultiple={false}
            onFilesAdd={(files) => {
              const file = Array.isArray(files) ? files[0] : files;
              if (file) {
                handleFileAdd(file as File);
              }
            }}
          />
        )}
        {value.file && (
          <InvoiceFileInput
            value={value.file}
            onChange={(file) => {
              // 새 파일로 교체하는 경우에도 발행일자/금액/BL 자동 추출
              if (isFile(file)) {
                handleFileAdd(file);
              } else {
                onChange({ ...value, file });
              }
            }}
            onDelete={() => onChange({ ...value, file: null })}
            isDisabled={isDisabled}
          />
        )}
      </FormControl>
    </Flex>
  );
}

function TransportationPaymentForm({
  agencyId,
  transportationId,
  existing,
  onSaved,
  onCancel,
}: {
  agencyId: number;
  transportationId: number;
  existing?: TransportationPayment | null;
  onSaved?: () => void;
  onCancel?: () => void;
}) {
  const { tTitle } = useI18nHelper();
  const { error: showError, warning: showWarning } = useAppToasts();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState<FormState>({
    issuedOn: new Date(),
    file: null,
    amount: "",
    isRemitted: false,
    remittedOn: null,
  });

  useEffect(() => {
    if (existing) {
      setForm({
        issuedOn: existing.issuedOn ? new Date(existing.issuedOn) : null,
        file: existing.file ?? null,
        amount: existing.amount != null ? String(existing.amount.value) : "",
        isRemitted: existing.isRemitted ?? false,
        remittedOn: existing.remittedOn ? new Date(existing.remittedOn) : null,
      });
    }
  }, [existing]);

  const { fire: fireCreate } = useCreateTransportationPayment();
  const { fire: fireUpdate } = useUpdateTransportationPayment();
  const { fire: fireStoreObject } = useStoreObject();

  const handleSave = useCallback(async () => {
    if (!form.issuedOn) {
      showWarning(tTitle("issued_on") + " 을(를) 입력해주세요.");
      return;
    }
    const amount = Number(form.amount);
    if (!form.amount || isNaN(amount)) {
      showWarning(tTitle("amount") + " 을(를) 입력해주세요.");
      return;
    }

    setIsSubmitting(true);
    try {
      let storedFile: Pick<StoredObject, "id" | "name"> | null = null;
      if (form.file) {
        if (isFile(form.file)) {
          const res = await fireStoreObject({}, { file: form.file });
          storedFile = { id: res.id, name: res.name };
        } else if (isStoredObject(form.file)) {
          storedFile = { id: form.file.id, name: form.file.name };
        }
      }

      const request = {
        issuedOn: form.issuedOn,
        file: storedFile,
        amount: { value: amount, currency: "KRW" } as Price,
        isRemitted: form.isRemitted,
        remittedOn: form.isRemitted ? form.remittedOn : null,
      };

      if (existing) {
        await fireUpdate(
          { agencyId, transportationId, paymentId: existing.id },
          request
        );
      } else {
        await fireCreate({ agencyId, transportationId }, request);
      }

      mutateIfMatched(
        new RegExp(`/agencies/\\d+/transportations/${transportationId}`)
      );
      onSaved?.();
    } catch (e) {
      showError(e);
    } finally {
      setIsSubmitting(false);
    }
  }, [form, existing, agencyId, transportationId, fireCreate, fireUpdate, fireStoreObject, showError, showWarning, tTitle, onSaved]);

  return (
    <Flex flexDirection={"column"} gap={"12px"} padding={"12px"}>
      <Grid templateColumns={"repeat(4, 1fr)"} gap={"12px"}>
          <FormControl>
            <FormLabel fontSize={"12px"} marginBottom={"4px"}>
              {tTitle("issued_on")}
            </FormLabel>
            <AppDatePicker
              value={form.issuedOn ?? undefined}
              showTimeInput={false}
              onChange={(value) => setForm((prev) => ({ ...prev, issuedOn: value ?? null }))}
              isDisabled={isSubmitting}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize={"12px"} marginBottom={"4px"}>
              {tTitle("amount")} (KRW)
            </FormLabel>
            <AppInput
              value={form.amount}
              type={"number"}
              onChange={(value) => setForm((prev) => ({ ...prev, amount: value ?? "" }))}
              isDisabled={isSubmitting}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize={"12px"} marginBottom={"4px"}>
              {tTitle("remittance_status")}
            </FormLabel>
            <AppSelect<string>
              name={tTitle("remittance_status")}
              width={"100%"}
              options={[
                { value: "false", name: tTitle("remittance_status.remittance_required") },
                { value: "true", name: tTitle("remittance_status.remitted") },
              ]}
              value={form.isRemitted ? "true" : "false"}
              onSelect={(value) => setForm((prev) => ({
                ...prev,
                isRemitted: value === "true",
                remittedOn: value === "true" ? prev.remittedOn ?? new Date() : null,
              }))}
              isDisabled={isSubmitting}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize={"12px"} marginBottom={"4px"}>
              {tTitle("remitted_on")}
            </FormLabel>
            <AppDatePicker
              name={""}
              value={form.remittedOn ?? undefined}
              showTimeInput={false}
              onChange={(value) => setForm((prev) => ({ ...prev, remittedOn: value ?? null }))}
              isDisabled={isSubmitting || !form.isRemitted}
            />
          </FormControl>
        </Grid>
        <FormControl>
          <FormLabel fontSize={"12px"} marginBottom={"4px"}>
            {tTitle("file")}
          </FormLabel>
          {!form.file && (
            <AppFileDragAndDropZone
              isMultiple={false}
              onFilesAdd={(value) => {
                const file = Array.isArray(value) ? value[0] : value;
                setForm((prev) => ({ ...prev, file }));
              }}
            />
          )}
          {form.file && (
            <InvoiceFileInput
              value={form.file}
              onChange={(file) => setForm((prev) => ({ ...prev, file }))}
              onDelete={() => setForm((prev) => ({ ...prev, file: null }))}
              isDisabled={isSubmitting}
            />
          )}
        </FormControl>
        <Flex justifyContent={"flex-end"} gap={"8px"}>
          {onCancel && (
            <AppButton isDisabled={isSubmitting} onClick={onCancel}>
              {tTitle("cancel")}
            </AppButton>
          )}
          <AppButton
            variant={"primary"}
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            onClick={handleSave}
          >
            {tTitle(existing ? "save" : "upload")}
          </AppButton>
        </Flex>
    </Flex>
  );
}

export default function TransportationPaymentFormControl({ agencyId, transportationId, existing, alwaysEdit }: Props) {
  const { tTitle, tCurrencyString, tLocalDateString } = useI18nHelper();
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing || alwaysEdit) {
    return (
      <AppPanel variant={"ghost"}>
        <Flex flexDirection={"column"} gap={"8px"}>
          <Text fontSize={"14px"} fontWeight={"600"} color={"#444440"}>
            {tTitle("transportation_payment")}
          </Text>
          <Flex
            flexDirection={"column"}
            gap={"12px"}
            padding={"12px"}
            border={"1px solid #E0E0DC"}
            borderRadius={"6px"}
          >
            <TransportationPaymentForm
              agencyId={agencyId}
              transportationId={transportationId}
              existing={existing}
              onSaved={alwaysEdit ? undefined : () => setIsEditing(false)}
              onCancel={alwaysEdit ? undefined : () => setIsEditing(false)}
            />
          </Flex>
        </Flex>
      </AppPanel>
    );
  }

  return (
    <AppPanel variant={"ghost"}>
      <Flex flexDirection={"column"} gap={"8px"}>
        <Flex alignItems={"center"} justifyContent={"space-between"}>
          <Text fontSize={"14px"} fontWeight={"600"} color={"#444440"}>
            {tTitle("transportation_payment")}
          </Text>
          {!existing && (
            <AppButton onClick={() => setIsEditing(true)}>
              {tTitle("upload")}
            </AppButton>
          )}
        </Flex>
        {existing && (
          <Flex
            flexDirection={"column"}
            gap={"8px"}
            padding={"12px"}
            border={"1px solid #E0E0DC"}
            borderRadius={"6px"}
            backgroundColor={"white"}
          >
            <Flex alignItems={"center"} justifyContent={"space-between"}>
              <Flex alignItems={"center"} gap={"8px"}>
                <Text fontSize={"13px"} fontWeight={"600"}>
                  {tCurrencyString(existing.amount)}
                </Text>
                <AppRemittanceStatusLabel
                  value={existing.isRemitted ? "REMITTED" : "UNREMITTED"}
                />
              </Flex>
              <AppButton onClick={() => setIsEditing(true)}>
                {tTitle("edit")}
              </AppButton>
            </Flex>
            <Flex gap={"16px"}>
              <Flex gap={"4px"} alignItems={"center"}>
                <Text fontSize={"12px"} color={"#6F6F6C"}>{tTitle("issued_on")}:</Text>
                <Text fontSize={"12px"}>{existing.issuedOn ? tLocalDateString(new Date(existing.issuedOn)) : "-"}</Text>
              </Flex>
              {existing.isRemitted && existing.remittedOn && (
                <Flex gap={"4px"} alignItems={"center"}>
                  <Text fontSize={"12px"} color={"#6F6F6C"}>{tTitle("remitted_on")}:</Text>
                  <Text fontSize={"12px"}>{tLocalDateString(new Date(existing.remittedOn))}</Text>
                </Flex>
              )}
            </Flex>
            {existing.file && (
              <Flex gap={"4px"} alignItems={"center"}>
                <Text fontSize={"12px"} color={"#6F6F6C"}>{tTitle("file")}:</Text>
                <Text fontSize={"12px"}>{existing.file.name}</Text>
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
    </AppPanel>
  );
}
